import {
  FilterValueDTO,
  IFragmentFragmentScreenDTO,
  ProteinFormulationDTO,
  SeedInfoDTO,
  WellIngDTO,
} from 'business/models/Models';
import SeedService from 'business/services/SeedService';
import { useEffect, useMemo, useState } from 'react';
import {
  GetProteinFormulation,
  getIngredientByIngIngTypeId,
  getIngredientStock,
  getNewId,
} from 'utils/Global';
import {
  IWellDropsData,
  useLatestSelectedSearchRow,
  useSearchDisplayFields,
  useSearchFilters,
  useSearchResult,
  useWellDropsWithIngredients,
} from 'redux/hooks/searchnFilter';
import { fieldsMap } from './utils';
import { IIngredientProteinInfo } from 'components/commandpanel/IngredientInfo/IngredientProteinInfo';
import { LayerIngredient } from 'business/models/Layer';
import { IStockRowProps } from 'components/commandpanel/RequiredStock/RequiredStock';
import {
  transformIIngredientProteinInfo,
  transformIIngredientWellInfoProps,
} from 'transformers/searchnFilter';
import { useRMDispatch } from 'redux/hooks';
import { resetSearchFilterValues } from 'redux/reducers/searchnFilterReducer';
import { usePrecisionFormatter } from 'CustomHooks/usePrecisionFormatter';

const getIngredients = (ings): IStockRowProps[] => {
  if (!ings) {
    return [];
  }
  return ings.map((ing) => {
    const gIng = getIngredientByIngIngTypeId(ing.ingredientIngredientTypeId);
    const gIngStock = getIngredientStock(ing.ingredientStockId);
    return {
      ingredientStockId: ing.ingredientStockId,
      name: gIng.longName,
      stocknum: gIngStock?.stockNumber,
      ph: gIngStock?.stockPh,
      volume: ing.volume,
      vendorpartnum: gIngStock?.vendorPartNumber,
    } as IStockRowProps;
  });
};

const getLayerIngredient = (ings): LayerIngredient[] => {
  if (!ings) {
    return [];
  }
  return ings.map((ing) => {
    const gIngStock = getIngredientStock(ing.ingredientStockId);
    const highPhIngStock = getIngredientStock(
      ing.highPhIngStockId ?? ing.highPhIngredientStockId,
    );
    return {
      ingredientColor: ing.hexColor,
      concentration: ing.concentration,
      concentrationUnit: ing.concentrationUnit,
      ingredientVolume: ing.volume,
      ingredientTypeName: ing.ingredientTypeName,
      ingredientStock: gIngStock,
      dropNumber: null,
      ingredientName: ing.ingredientName,
      highPHVolume: ing.highPHVolume,
      highPhIngStock: highPhIngStock,
    } as LayerIngredient;
  });
};

const EMPTY_SEED_IDS: number[] = [];

const getSeedVolumeById = (
  seedIds: number[],
  dropSeedVolume?: number | null,
): Record<number, number> => {
  if (seedIds.length === 1) {
    return { [seedIds[0]]: dropSeedVolume ?? 0 };
  }
  return {};
};

export const useWellAndDropDetails = (): {
  wellProps: any;
  proteinProps: any;
  additivesProps: any;
} | null => {
  const latestSelectedRow = useLatestSelectedSearchRow();
  const wells_ = useWellDropsWithIngredients();
  const [seedInfos, setSeedInfos] = useState<SeedInfoDTO[]>([]);
  const formatWithPrecision = usePrecisionFormatter();

  const selection = useMemo(() => {
    if (!latestSelectedRow || !wells_) {
      return null;
    }

    const selectedWells = wells_.filter(
      (well: WellIngDTO) => well.wellId === latestSelectedRow[fieldsMap.wellId],
    );
    if (selectedWells.length === 0) {
      return null;
    }

    const drops = selectedWells
      .map((w) => w.dropList.filter((d) => d.dropId !== 0))
      .flat();
    const selectedDrop = drops.find(
      (drop) => drop.dropId === latestSelectedRow[fieldsMap.wellDropId],
    );
    if (!selectedDrop) {
      return null;
    }

    return {
      selectedWells,
      drops,
      selectedDrop,
      wellLayers: selectedWells[0].wellLayerList,
    };
  }, [latestSelectedRow, wells_]);

  const seedIds = selection?.selectedDrop?.seedIDs ?? EMPTY_SEED_IDS;
  const seedIdsKey = seedIds.join(',');

  useEffect(() => {
    setSeedInfos([]);
    const ids = seedIdsKey ? seedIdsKey.split(',').map(Number) : [];
    if (!ids.length) {
      return;
    }

    let cancelled = false;
    const seedService = new SeedService();
    seedService
      .getSeeds(ids)
      .then((infos) => {
        if (!cancelled) {
          setSeedInfos(infos ?? []);
        }
      })
      .catch((e) => {
        console.error('Failed to fetch seed info', e);
        if (!cancelled) {
          setSeedInfos([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [seedIdsKey]);

  return useMemo(() => {
    if (!latestSelectedRow || !selection) {
      return null;
    }

    const { selectedWells, drops, selectedDrop, wellLayers } = selection;

    let dropIngredients = [];
    let dropProteinVolume = 0,
      dropWellVolume = 0;
    let wellLayerIngredients = [];

    const dropsWellData: IWellDropsData = {
      wellVolume: selectedDrop.dropWellVolume,
      dropProteinVolume: selectedDrop.dropProteinVolume,
      seedId: selectedDrop.seedIDs?.[0] ?? null,
      dropNumber: selectedDrop.dropNumber,
    };

    selectedDrop.dropLayerList.forEach((dropLayer) => {
      dropIngredients = [...dropIngredients, ...dropLayer.dropLayerIngredientList];
    });

    drops.forEach((drop) => {
      dropProteinVolume += drop.dropProteinVolume;
      dropWellVolume += drop.dropWellVolume;
    });

    const dropVolumes = { dropProteinVolume, dropWellVolume };

    wellLayers.forEach((wellLayer) => {
      wellLayerIngredients = [
        ...wellLayerIngredients,
        ...wellLayer.wellLayerIngredientList,
      ];
    });

    const wellName = `${selectedWells[0].rowLetter}${selectedWells[0].columnNumber}`;
    const wellVolume = selectedWells[0].wellVolume;
    const wellWaterVolume = selectedWells[0].wellWaterVolume;
    const wellNameVolume = { wellName, wellVolume, wellWaterVolume };

    const additivesProps = {
      additives: getIngredients(dropIngredients),
      dropNumber: latestSelectedRow[fieldsMap.dropNumber],
    };

    const pfInfo: ProteinFormulationDTO = GetProteinFormulation(
      latestSelectedRow[fieldsMap.proteinFormulationId],
    );

    const fragmentIds = selectedDrop.dropLayerList?.flatMap((dll) =>
      dll.dropLayerFragmentList?.flatMap((dlf) => {
        return {
          fragmentId: dlf.fragmentId,
          fragmentScreenId: selectedDrop.fragmentScreenId,
        } as IFragmentFragmentScreenDTO;
      }),
    );

    const proteinProps: IIngredientProteinInfo = transformIIngredientProteinInfo(
      false,
      pfInfo,
      dropsWellData,
      seedInfos,
    );
    proteinProps.fragmentIds = fragmentIds;
    proteinProps.well = selectedWells[0];
    proteinProps.seedVolumeById = getSeedVolumeById(seedIds, selectedDrop.dropSeedVolume);
    const wellLayerIngs = getLayerIngredient(wellLayerIngredients);

    wellLayerIngs?.forEach((wellLayerIng) => {
      wellLayerIng.dropNumber = latestSelectedRow[fieldsMap.dropNumber];
    });
    const wellProps = transformIIngredientWellInfoProps(
      wellName,
      false,
      dropVolumes,
      wellLayerIngs,
      wellNameVolume,
      formatWithPrecision,
    );

    return { proteinProps, wellProps, additivesProps };
  }, [latestSelectedRow, selection, seedInfos, seedIdsKey, formatWithPrecision]);
};

export const usePrimaryFilterEffect = () => {
  const displayFields = useSearchDisplayFields();
  const filters = useSearchFilters();
  const searchResult = useSearchResult();
  const { primaryFilter: primaryFilterId } = filters;
  const dispatch = useRMDispatch();
  const primaryFilter = filters.data.find((f) => f.id === primaryFilterId);

  useEffect(() => {
    if (primaryFilter) {
      const displayField = displayFields.find(
        (df) => df.searchFieldId === primaryFilter.searchFieldId,
      );
      const filteredResult = searchResult.rows.items.filter((row) =>
        primaryFilter.values
          .map((fv) => fv.value)
          .includes(row[displayField.columnHeader]?.toString()),
      );
      const restFilters = filters.data.filter((f) => f.id !== primaryFilterId);
      restFilters.forEach((filter) => {
        const displayField = displayFields.find(
          (df) => df.searchFieldId === filter.searchFieldId,
        );
        const values: FilterValueDTO[] = [];
        filteredResult.forEach((row) => {
          if (!values.find((fv) => fv.value == row[displayField.columnHeader])) {
            values.push({
              id: getNewId(),
              filterId: filter.id,
              value: row[displayField.columnHeader]?.toString(),
            } as FilterValueDTO);
          }
        });
        dispatch(resetSearchFilterValues({ filterId: filter.id, values }));
      });
    }
  }, [primaryFilter?.values]);
};
